/**
 * The words a new proposal opens with, written from what the portal knows.
 *
 * A proposal used to open with the pages nothing wrote left out: no letter,
 * no challenge, no approach, no team. This fills them on creation from data:
 * the client or lead (name, company, what they asked for, their note), the
 * services sold and their weeks on the price list, the policies, the case
 * studies and testimonials the studio has saved, the owner and the
 * contractors whose specialty fits the work.
 *
 * Rules it keeps:
 *  - It never writes a price. Figures stay in the investment section; the one
 *    place a figure appears here is the payment clause, and that is the
 *    frozen split of this proposal's own total.
 *  - It never invents a fact: no quotes, no statistics. Quotes and case
 *    studies are only ever copied from what the studio stored.
 *  - Everything it writes is stored on the document, so the studio edits it
 *    like anything typed by hand. Each generated part keeps a fingerprint in
 *    `content.generated`, so regenerating can tell a hand edit from its own
 *    text and ask first.
 *
 * Where each part lives:
 *   letter     overview.data.body
 *   challenge  overview.data.challenges / overview.data.success
 *   glance     overview.data.glance (the four "what you get" lines)
 *   approach   scope.data.approach (the five stages' words; their weeks are
 *              worked out live from the services, src/lib/proposalSchedule.js)
 *   cases      page sections of kind case_study, and proof.data.quotes
 *   team       content.team
 *   terms      terms.data.items / terms.data.faq / terms.data.working
 */

import { STANDARD_COPY } from './proposalLooks'
import { scheduleServices } from './proposalSchedule'
import { contactFirstName } from './proposalVars'
import {
  GROUP_OF_SERVICE, GROUP_OF_PROGRAM, GROUP_ORDER, GROUP_WORDS, PATIENT_WORDS,
  CHALLENGES, SUCCESS, LETTER_WHY, GLANCE_LINE, GLANCE_PAD, STAGES, STAGE_STRATEGY_EXISTING,
  CONTRACTOR_TASK, SPECIALTY_SERVICES,
} from './proposalGenerateCopy'

const text = (v) => String(v ?? '').trim()

/** The parts that can be generated, in the order "Regenerate all" runs them. */
export const GENERATED = [
  { key: 'letter', label: 'Letter' },
  { key: 'challenge', label: 'The challenge' },
  { key: 'glance', label: 'At a glance' },
  { key: 'approach', label: 'Approach' },
  { key: 'cases', label: 'Case studies' },
  { key: 'team', label: 'Team' },
  { key: 'terms', label: 'Terms, working together and questions' },
]
export const GENERATED_KEYS = GENERATED.map(g => g.key)

/** Which generated parts a section type holds, for the studio's per-section button. */
export const KEYS_FOR_SECTION = {
  overview: ['letter', 'challenge', 'glance'],
  scope: ['approach'],
  proof: ['cases'],
  page: ['cases'],
  terms: ['terms'],
  about: ['team'],
}

/* ── Context ─────────────────────────────────────────────────────────────── */

/**
 * Everything the generator reads, gathered once.
 *
 * `record` is the client or lead row (any of name, company, email, industry,
 * project_type, budget, timeline, description, notes). `catalog` is
 * loadCatalog()'s result: programs, services, policies, and the extras it
 * reads for this (contractors, caseStudies, testimonials, settings).
 */
export function generationContext({ record = null, catalog = {}, settings = {}, packageName = '', validityDays = 30 } = {}) {
  const r = record || {}
  const s = { ...(catalog.settings || {}), ...Object.fromEntries(Object.entries(settings || {}).filter(([, v]) => text(v))) }
  const company = text(r.company)
  const contact = text(r.name)
  const ownerName = text(s.owner_name) || 'Efren Cavazos'
  const haystack = [company, r.industry, r.project_type, r.description, r.notes, typeof r.notes_list === 'object' ? '' : r.notes_list]
    .map(text).join(' ')
  return {
    company,
    contact,
    contactFirst: contactFirstName(contact),
    who: company || contact,
    projectType: text(r.project_type),
    description: text(r.description),
    budget: text(r.budget),
    timeline: text(r.timeline),
    audience: PATIENT_WORDS.test(haystack) ? 'patients' : 'clients',
    ownerName,
    ownerFirst: ownerName.split(/\s+/)[0],
    packageName: text(packageName),
    validityDays: Number(validityDays) || 30,
    catalog: {
      programs: catalog.programs || [],
      services: catalog.services || [],
      policies: catalog.policies || [],
      contractors: catalog.contractors || [],
      caseStudies: catalog.caseStudies || [],
      testimonials: catalog.testimonials || [],
    },
    haystack,
  }
}

/** Slots filled: {company}, {aud}, {Aud}, {owner}. */
const fill = (s, ctx) => String(s)
  .replace(/\{company\}/g, ctx.company || 'you')
  .replace(/\{aud\}/g, ctx.audience)
  .replace(/\{Aud\}/g, ctx.audience[0].toUpperCase() + ctx.audience.slice(1))
  .replace(/\{owner\}/g, ctx.ownerFirst)
  .replace(/\{firms\}/g, ctx.audience === 'patients' ? 'practices' : 'firms')

/* ── Reading the document ────────────────────────────────────────────────── */

const findIndex = (doc, type) => (doc.sections || []).findIndex(s => s.type === type)
const dataOf = (doc, type) => (doc.sections || []).find(s => s.type === type)?.data || {}

/** The services the proposal sells: scope items and investment rows with an id. */
function soldServices(doc, ctx) {
  const byId = Object.fromEntries(ctx.catalog.services.map(s => [s.id, s]))
  const items = (dataOf(doc, 'scope').items || []).filter(i => text(i?.title) || text(i?.description))
  const rows = (dataOf(doc, 'investment').rows || []).filter(r => text(r?.label))
  const seen = new Set()
  const out = []
  for (const x of [...items, ...rows]) {
    const id = x.service_id || null
    const key = id || text(x.title || x.label).toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    const svc = id ? byId[id] : null
    out.push({
      id,
      title: text(x.title || x.label || svc?.service),
      program_id: x.program_id || svc?.program_id || null,
      retainer: x.kind === 'retainer' || svc?.price_type === 'recurring',
    })
  }
  return out
}

const groupOf = (s) => GROUP_OF_SERVICE[s.id] || GROUP_OF_PROGRAM[s.program_id] || null

/** The groups the proposal sells, in the order the work happens; else what the request points to. */
function groupsOf(services, ctx) {
  const sold = new Set(services.map(groupOf).filter(Boolean))
  if (!sold.size) {
    for (const g of GROUP_ORDER) if (GROUP_WORDS[g].test(`${ctx.projectType} ${ctx.description}`)) sold.add(g)
  }
  return GROUP_ORDER.filter(g => sold.has(g))
}

const listWords = (names) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`)

function totalWeeksOf(doc, ctx) {
  const byId = Object.fromEntries(ctx.catalog.services.map(s => [s.id, s]))
  return scheduleServices(dataOf(doc, 'scope').items || [], byId).totalWeeks
}

/** The frozen payment split, as words: "50% to start and 50% at final delivery". */
function splitWords(doc) {
  const t = dataOf(doc, 'investment').totals
  const sched = (t?.schedule || []).filter(p => Number(p.amount) > 0)
  if (!sched.length) return ''
  return listWords(sched.map(p => `${p.pct}% ${text(p.label).toLowerCase()}`))
}

const excerpt = (s, max = 220) => {
  const v = text(s).replace(/\s+/g, ' ').replace(/[“”"]/g, '')
  if (v.length <= max) return v.replace(/[.!?]*$/, '.')
  const cut = v.slice(0, max)
  return `${cut.slice(0, cut.lastIndexOf(' ') > 0 ? cut.lastIndexOf(' ') : max).replace(/[,;:.]+$/, '')}…`
}

/* ── The parts ───────────────────────────────────────────────────────────── */

/** The letter, first person from the owner. The renderers add "Dear …" and the signature. */
export function letterText(doc, ctx) {
  const services = soldServices(doc, ctx)
  const groups = groupsOf(services, ctx)
  const weeks = totalWeeksOf(doc, ctx)
  const paras = []

  const asked = ctx.projectType && !/not sure/i.test(ctx.projectType) ? ctx.projectType.split(':').pop().trim() : ''
  if (asked) {
    paras.push(`Thank you for getting in touch about ${asked.toLowerCase().replace(/\b(crm|seo|cmo|ux|ui)\b/g, m => m.toUpperCase()).replace(/[.\s]+$/, '')}. This proposal sets out how I would approach it for ${ctx.company || 'you'}.`)
  } else {
    paras.push(`Thank you for the chance to put this together${ctx.company ? ` for ${ctx.company}` : ''}. This proposal sets out what I would do, how long it takes and what it costs.`)
  }

  if (ctx.description) paras.push(`In your note you wrote: “${excerpt(ctx.description)}” I have built the plan around that.`)

  const names = services.filter(s => s.title).map(s => s.title)
  if (names.length) {
    const what = ctx.packageName && names.length > 1
      ? `The plan is ${ctx.packageName}: ${listWords(names)}.`
      : `The plan is ${listWords(names)}.`
    const when = weeks ? ` It runs about ${weeks} week${weeks === 1 ? '' : 's'} from kickoff, and each stage ends with something you approve before the next one starts.` : ' Each stage ends with something you approve before the next one starts.'
    paras.push(what + when)
  } else {
    paras.push('The scope, the schedule and the investment are set out on the pages that follow.')
  }

  const why = groups.map(g => fill(LETTER_WHY[g], ctx))
  if (why.length) paras.push(why.join(' '))

  const timing = ctx.timeline ? ` On timing, you said “${excerpt(ctx.timeline, 80).replace(/\.$/, '')}”, and the timeline page shows how the weeks run.` : ''
  paras.push(ctx.budget
    ? `I have kept the budget you mentioned in mind. The investment page shows exactly where it goes, and the price is fixed once you sign.${timing}`
    : `The price is fixed once you sign, so there are no surprises.${timing}`)

  paras.push('If anything here does not fit, tell me and I will change it before you sign. I would be glad to do this with you.')
  return paras.join('\n\n')
}

/** Three challenges and three signs of success, one from each group in turn. */
export function challengeData(doc, ctx) {
  const groups = groupsOf(soldServices(doc, ctx), ctx)
  if (!groups.length) return { challenges: [], success: [] }
  const pick = (lib) => {
    const out = []
    for (let round = 0; out.length < 3 && round < 3; round += 1) {
      for (const g of groups) {
        if (out.length >= 3) break
        const row = lib[g][round]
        if (row) out.push({ title: fill(row[0], ctx), body: fill(row[1], ctx) })
      }
    }
    return out
  }
  return { challenges: pick(CHALLENGES), success: pick(SUCCESS) }
}

/** Four "what you get" lines from the services, padded with what every project includes. */
export function glanceLines(doc, ctx) {
  const services = soldServices(doc, ctx)
  const lines = services.map(s => fill(GLANCE_LINE[s.id] || s.title, ctx)).filter(Boolean)
  for (const pad of GLANCE_PAD) {
    if (lines.length >= 4) break
    lines.push(fill(pad, ctx))
  }
  return lines.slice(0, 4)
}

/** The five stages' words, tailored to what is sold. Weeks are drawn live. */
export function approachData(doc, ctx) {
  const services = soldServices(doc, ctx)
  const ids = new Set(services.map(s => s.id))
  const design = services.filter(s => ['identity', 'website', 'growth'].includes(groupOf(s)) && !s.retainer).map(s => s.title)
  return STAGES.map(([name, what, time]) => {
    let w = fill(what, ctx)
    let t = time
    if (name === 'Strategy' && !ids.has('positioning-messaging') && !ids.has('brand-architecture')) w = STAGE_STRATEGY_EXISTING
    if (name === 'Discovery' && !ids.has('positioning-messaging')) t = 'a half-day kickoff'
    if (name === 'Design' && design.length) w = `The work itself (${listWords(design)}), shown on real things: your site, your documents, your space.`
    return { name, what: w, time: t }
  })
}

/** The team: the owner always, then up to two contractors whose specialty fits the work. */
export function teamData(doc, ctx) {
  const services = soldServices(doc, ctx)
  const ids = services.map(s => s.id).filter(Boolean)
  const titleOf = Object.fromEntries(services.map(s => [s.id, s.title]))
  const hasStrategy = ids.some(id => GROUP_OF_SERVICE[id] === 'strategy')
  const owner = {
    name: ctx.ownerName,
    role: 'Founder and creative director',
    line: 'Leads the work from the first conversation to the handover.',
    bio: '',
    tasks: [
      hasStrategy ? 'Leads strategy and every workshop' : 'Leads every workshop and review',
      'Sets the creative direction',
      'Presents each stage for approval',
      'Is your one point of contact',
    ],
    owner: true,
  }
  const people = ctx.catalog.contractors
    .filter(c => text(c?.name) && (!c.status || /^active$/i.test(text(c.status))))
    .map(c => {
      const spec = text(c.specialty)
      const fits = new Set()
      for (const [re, list] of SPECIALTY_SERVICES) if (re.test(spec)) list.forEach(id => fits.add(id))
      const matched = ids.filter(id => fits.has(id))
      return { c, spec, matched }
    })
    .filter(x => x.matched.length)
    .sort((a, b) => b.matched.length - a.matched.length)
    .slice(0, 2)
    .map(({ c, spec, matched }) => ({
      name: text(c.name),
      role: spec || 'Specialist',
      line: `Works with ${ctx.ownerFirst} on ${listWords(matched.map(id => titleOf[id]).filter(Boolean))}.`,
      bio: '',
      tasks: [...matched.slice(0, 3).map(id => fill(CONTRACTOR_TASK[id] || `Works on ${titleOf[id]}`, ctx)), `Reports to ${ctx.ownerFirst}, who reviews everything before you see it`],
      contractor_id: c.id || null,
    }))
  return [owner, ...people]
}

/** Case studies that match the work or the field, best first, at most two. */
export function pickCaseStudies(doc, ctx, max = 2) {
  const services = soldServices(doc, ctx)
  const ids = new Set(services.map(s => s.id).filter(Boolean))
  const groups = groupsOf(services, ctx)
  return ctx.catalog.caseStudies
    .filter(c => text(c?.title) && (text(c.body) || text(c.description) || (c.results || []).length))
    .map(c => {
      const hay = [c.title, c.category, c.client, c.body, c.description].map(text).join(' ')
      let score = 0
      if (c.service_id && ids.has(c.service_id)) score += 3
      for (const g of groups) if (GROUP_WORDS[g].test(hay)) score += 2
      if (ctx.audience === 'patients' && PATIENT_WORDS.test(hay)) score += 2
      return { c, score }
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map(x => x.c)
}

const caseSection = (c) => {
  const results = (c.results || []).map(text).filter(Boolean)
  const body = [text(c.body) || text(c.description), results.length ? results.map(r => `· ${r}`).join('\n') : ''].filter(Boolean).join('\n\n')
  return {
    id: `page-${Math.random().toString(36).slice(2, 9)}`,
    type: 'page',
    data: {
      kind: 'case_study',
      title: text(c.title),
      client: text(c.client),
      body,
      ...(text(c.image_url) ? { image_url: text(c.image_url) } : {}),
      generated: 'cases',
      case_ref: c.id || text(c.title),
    },
  }
}

/** Terms from the policies with this proposal's split, the working-together lists and the questions. */
export function termsData(doc, ctx) {
  const policies = ctx.catalog.policies.filter(p => p && p.client_facing !== false)
  const pol = (id) => policies.find(p => p.id === id && text(p.body))
  const split = splitWords(doc)
  const monthly = Number(dataOf(doc, 'investment').totals?.monthly) > 0
  const payment = split
    ? `${split.replace(/^\w/, c => c.toUpperCase())}. Due within 15 days, by card or bank transfer.${monthly ? ' Ongoing services are billed monthly in advance.' : ''}`
    : monthly
      ? 'Billed monthly in advance. Each invoice is due within 15 days, by card or bank transfer.'
      : (pol('payment-terms') ? text(pol('payment-terms').body) : 'Invoiced as set out on the investment page. Due within 15 days, by card or bank transfer.')
  const std = Object.fromEntries(STANDARD_COPY.terms)
  const validity = `Prices and dates hold for ${ctx.validityDays} days from the cover date.`
  const name = (p) => text(p.name) || p.id.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase())

  const items = [{ title: 'Payment', body: payment }]
  const fromPolicies = ['scope-protection', 'contractor-costs', 'rush-work'].map(pol).filter(Boolean)
  for (const p of fromPolicies) items.push({ title: name(p), body: text(p.body) })
  const skip = new Set(['Payment'])
  if (pol('scope-protection')) { skip.add('Revisions'); skip.add('Changes to scope') }
  if (pol('contractor-costs')) skip.add('Third-party costs')
  for (const [title, body] of STANDARD_COPY.terms) {
    if (skip.has(title) || items.length >= 12) continue
    items.push({ title, body: title === 'This proposal' ? validity : body })
  }

  const rounds = pol('scope-protection') ? text(pol('scope-protection').body) : STANDARD_COPY.faq[1][1]
  const faq = [
    { q: STANDARD_COPY.faq[0][0], a: STANDARD_COPY.faq[0][1] },
    { q: STANDARD_COPY.faq[1][0], a: rounds },
    { q: STANDARD_COPY.faq[2][0], a: std.Ownership ? 'You do, in full, once the final invoice is paid.' : STANDARD_COPY.faq[2][1] },
  ]

  const services = soldServices(doc, ctx)
  const interviews = services.some(s => s.id === 'positioning-messaging')
  const weNeed = STANDARD_COPY.weNeed
    .filter(([k]) => interviews || k !== 'Interviews in week one')
    .map(([k, v]) => ({ title: k, body: v }))
  const youGet = STANDARD_COPY.youGet.map(([k, v]) => ({
    title: k,
    body: k === 'Senior attention' ? `${ctx.ownerFirst} leads the work and presents every stage.` : v,
  }))
  return { items, faq, working: { weNeed, youGet } }
}

/* ── Fingerprints: was it edited since it was generated? ─────────────────── */

const hash = (s) => {
  let h = 5381
  const str = String(s)
  for (let i = 0; i < str.length; i += 1) h = ((h << 5) + h + str.charCodeAt(i)) | 0
  return (h >>> 0).toString(36)
}

/** What a part currently says, as one string. */
function currentOf(key, doc) {
  const ov = dataOf(doc, 'overview')
  switch (key) {
    case 'letter': return text(ov.body)
    case 'challenge': return JSON.stringify([ov.challenges || [], ov.success || []])
    case 'glance': return JSON.stringify(ov.glance || [])
    case 'approach': return JSON.stringify(dataOf(doc, 'scope').approach || [])
    case 'cases': return JSON.stringify([
      (doc.sections || []).filter(s => s.type === 'page' && s.data?.generated === 'cases').map(s => [s.data.title, s.data.body]),
      (dataOf(doc, 'proof').quotes || []).filter(q => q.generated).map(q => [q.quote, q.name]),
    ])
    case 'team': return JSON.stringify(doc.team || [])
    case 'terms': {
      const t = dataOf(doc, 'terms')
      return JSON.stringify([t.items || [], t.faq || [], t.working || null])
    }
    default: return ''
  }
}

const EMPTY = {
  letter: '', challenge: '[[],[]]', glance: '[]', approach: '[]', cases: '[[],[]]', team: '[]', terms: '[[],[],null]',
}

export const fingerprint = (key, doc) => hash(currentOf(key, doc))

/**
 * True when a part holds words the generator did not write: edited since it
 * was generated, or written by hand before it ever was. An empty part is never
 * edited.
 */
export function isEdited(key, doc) {
  const now = currentOf(key, doc)
  if (now === EMPTY[key]) return false
  const last = doc?.generated?.[key]
  return !last || last !== hash(now)
}

/* ── Writing it onto the document ────────────────────────────────────────── */

const withSection = (doc, type, fn, { after = null } = {}) => {
  let sections = [...(doc.sections || [])]
  let i = sections.findIndex(s => s.type === type)
  if (i < 0) {
    const s = { id: `${type}-${Math.random().toString(36).slice(2, 9)}`, type, data: {} }
    const at = after ? sections.findIndex(x => x.type === after) : -1
    sections = at >= 0 ? [...sections.slice(0, at + 1), s, ...sections.slice(at + 1)] : [...sections, s]
    i = sections.findIndex(x => x.id === s.id)
  }
  sections[i] = { ...sections[i], data: fn({ ...(sections[i].data || {}) }) }
  return { ...doc, sections }
}

/** One part, rebuilt from the data and stamped. Prices and line items are never touched. */
export function generatePart(key, doc, ctx) {
  let next = doc
  switch (key) {
    case 'letter':
      next = withSection(doc, 'overview', d => ({ ...d, body: letterText(doc, ctx) }), { after: 'cover' })
      break
    case 'challenge': {
      const { challenges, success } = challengeData(doc, ctx)
      next = withSection(doc, 'overview', d => ({ ...d, challenges, success }), { after: 'cover' })
      break
    }
    case 'glance':
      next = withSection(doc, 'overview', d => ({ ...d, glance: glanceLines(doc, ctx) }), { after: 'cover' })
      break
    case 'approach':
      next = findIndex(doc, 'scope') >= 0 ? withSection(doc, 'scope', d => ({ ...d, approach: approachData(doc, ctx) })) : doc
      break
    case 'cases': {
      const picked = pickCaseStudies(doc, ctx)
      const kept = (doc.sections || []).filter(s => !(s.type === 'page' && s.data?.generated === 'cases'))
      const pages = picked.map(caseSection)
      const at = kept.findIndex(s => s.type === 'terms')
      const sections = at >= 0 ? [...kept.slice(0, at), ...pages, ...kept.slice(at)] : [...kept, ...pages]
      next = { ...doc, sections }
      if (findIndex(next, 'proof') >= 0) {
        const clients = new Set(picked.map(c => text(c.client).toLowerCase()).filter(Boolean))
        const quotes = ctx.catalog.testimonials
          .filter(t => text(t?.quote))
          .sort((a, b) => Number(clients.has(text(b.company).toLowerCase())) - Number(clients.has(text(a.company).toLowerCase())))
          .slice(0, 2)
          .map(t => ({ quote: text(t.quote), name: text(t.name), company: text(t.company), generated: true }))
        next = withSection(next, 'proof', d => ({ ...d, quotes: [...(d.quotes || []).filter(q => !q.generated), ...quotes] }))
      }
      break
    }
    case 'team':
      next = { ...doc, team: teamData(doc, ctx) }
      break
    case 'terms': {
      const t = termsData(doc, ctx)
      next = findIndex(doc, 'terms') >= 0 ? withSection(doc, 'terms', d => ({ ...d, items: t.items, faq: t.faq, working: t.working })) : doc
      break
    }
    default:
      return doc
  }
  return { ...next, generated: { ...(next.generated || {}), [key]: fingerprint(key, next) } }
}

/**
 * Several parts at once. By default a part already holding words the
 * generator did not write is left alone; `force` rebuilds it anyway.
 */
export function generateAll(doc, ctx, { only = GENERATED_KEYS, force = false } = {}) {
  let next = doc
  for (const key of only) {
    if (!force && isEdited(key, next)) continue
    next = generatePart(key, next, ctx)
  }
  return next
}
